/**
 * soundFX.js
 * Lightweight UI sound effect system using Web Audio API.
 *
 * Why Web Audio API instead of HTMLAudioElement cloneNode():
 * Mobile browsers (iOS Safari in particular) impose a hard cap on the number
 * of concurrent / cloned Audio elements and silently stop playing new ones
 * after the first few triggers.  BufferSourceNodes created from an
 * AudioContext have no such limit — each is a lightweight, fire-and-forget
 * object that is garbage-collected automatically when playback ends.
 *
 * Strategy:
 *  • Audio files are fetched eagerly as raw ArrayBuffers (no user-gesture
 *    needed for fetch).
 *  • The AudioContext is created lazily on the first playSound() call, which
 *    always happens inside a user-gesture handler, satisfying the autoplay
 *    policy on iOS/Android.
 *  • Each AudioBuffer is decoded once and cached; subsequent plays are instant.
 */

const SOUNDS = {
  PlotCourse:             '/UI SoundFX/PlotCourse.aac',
  CourseDraw_Short:       '/UI SoundFX/CourseDraw_short.aac',
  LaunchMission:          '/UI SoundFX/LaunchMission.aac',
  AbandonMission:         '/UI SoundFX/AbandonMission.aac',
  HideCourse:             '/UI SoundFX/HideCourse.aac',
  TrackVehicle:           '/UI SoundFX/TrackVehicle.aac',
  JoinOurMission_Open:    '/UI SoundFX/JoinOurMission_Open.aac',
  JoinOurMission_Close:   '/UI SoundFX/JoinOurMission_Close.aac',
  MissionObjective_Open:  '/UI SoundFX/MissionObjective_Open.aac',
  MissionObjective_Close: '/UI SoundFX/MissionObjective_Close.aac',
  DataPoint_Open:         '/UI SoundFX/DataPoint_Open.aac',
  DataPoint_Close:        '/UI SoundFX/DataPoint_Close.aac',
  DataBeep:               '/UI SoundFX/DataBeep.aac',
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _userMuted = false
let _muted     = false

/** Single shared AudioContext — created lazily after the first user gesture. */
let audioCtx = null

/**
 * Raw ArrayBuffers fetched at module load.
 * fetch() does NOT require a user gesture, so this runs immediately.
 */
const rawBuffers = {}

/**
 * Decoded AudioBuffers, cached after the first decode.
 * Decoding requires an AudioContext (i.e. a user gesture), so it happens
 * lazily inside getDecodedBuffer().
 */
const decodedBuffers = {}

// Fetch all audio files up-front so they are ready when the user first
// interacts with the page.
if (typeof window !== 'undefined') {
  Object.entries(SOUNDS).forEach(([name, src]) => {
    fetch(src)
      .then(r => r.arrayBuffer())
      .then(ab => { rawBuffers[name] = ab })
      .catch(() => { /* non-critical — playSound will simply be a no-op */ })
  })
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Returns the shared AudioContext, creating or resuming it as needed.
 * Must be called from within (or after) a user-gesture to satisfy iOS policy.
 */
async function getAudioContext() {
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return null

  if (!audioCtx) {
    audioCtx = new Ctor()
  }

  // iOS suspends the context whenever the page loses focus; resume it.
  if (audioCtx.state === 'suspended') {
    await audioCtx.resume()
  }

  return audioCtx
}

/**
 * Returns the decoded AudioBuffer for `name`, decoding and caching it on
 * first access.  Returns null if the raw data has not yet arrived or if
 * decoding fails.
 */
async function getDecodedBuffer(name) {
  if (decodedBuffers[name]) return decodedBuffers[name]

  const raw = rawBuffers[name]
  if (!raw) return null

  const ctx = await getAudioContext()
  if (!ctx) return null

  try {
    // slice() produces a copy so decodeAudioData cannot detach our stored
    // rawBuffer (some older browsers transfer/detach the input ArrayBuffer).
    decodedBuffers[name] = await ctx.decodeAudioData(raw.slice(0))
    return decodedBuffers[name]
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Play a UI sound effect by name.
 *
 * The function is async but callers do NOT need to await it — it is
 * intentionally fire-and-forget.  All errors are caught internally.
 *
 * @param {string} name         - Key from the SOUNDS map
 * @param {number} volume       - 0.0 – 1.0  (default 0.8)
 * @param {number} playbackRate - Playback speed multiplier (default 1.0)
 */
export async function playSound(name, volume = 0.8, playbackRate = 1.0) {
  if (_muted) return

  if (!SOUNDS[name]) {
    if (import.meta.env.DEV) console.warn(`[soundFX] Unknown sound: "${name}"`)
    return
  }

  try {
    const ctx = await getAudioContext()
    if (!ctx) return

    const buffer = await getDecodedBuffer(name)
    if (!buffer) return

    // BufferSourceNodes are single-use and GC'd automatically after playback —
    // no bookkeeping or pooling needed, and no browser cap on concurrent nodes.
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.playbackRate.value = playbackRate

    const gain = ctx.createGain()
    gain.gain.value = volume

    source.connect(gain)
    gain.connect(ctx.destination)
    source.start(0)
  } catch {
    // Silently swallow any playback errors (e.g. context closed, decode race)
  }
}

export function setMuted(muted) {
  _userMuted = muted
  _muted     = muted
}

export function isMuted() {
  return _muted
}

// ---------------------------------------------------------------------------
// Visibility / focus muting
// ---------------------------------------------------------------------------

// Silence SFX when the tab/window loses visibility or focus,
// while respecting the user's manual mute preference.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    _muted = document.hidden || _userMuted
  })
  window.addEventListener('blur',  () => { _muted = true })
  window.addEventListener('focus', () => { _muted = _userMuted || document.hidden })
}

export default playSound